/* @flow */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

// Styles
import styles from './ExpressionOfGratitudePostBraindate.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Hooks
import useSelf from 'src/shared/domain/user/hook/useSelf';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';

// Utils
import messages from 'src/shared/app/expression-of-gratitude/l10n/expressionOfGratitudel10n.json';
import { getUserId } from '@braindate/domain/lib/user/util';
import { generatePath } from 'src/shared/app/base/route/util/routeUtils';
import { getBraindateId } from '@braindate/domain/lib/braindate/util';
import {
  dismissNotification,
  dismissNotificationsBundle,
} from 'src/shared/app/notification/action/uiNotificationActions';
import { getBraindateOtherParticipants } from 'src/shared/domain/braindate/util/braindateUtil';
import {
  newExpressionOfGratitudeRoute,
  newExpressionOfGratitudeWithBraindateRoute,
  newExpressionOfGratitudeWithBraindateAndUserRoute,
  homeRoute,
} from 'src/shared/app/base/route/setting/routeSettings';

// Components
import Modal from 'src/shared/app/base/modal/component/Modal';
import ExpressionOfGratitudeIcon from 'src/shared/ui/component/icon/ExpressionOfGratitudeIcon';

// Types
import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';
import { useNavigate } from 'react-router';
import Markdown from 'src/shared/app/base/component/text/Markdown';

type Props = {
  notification: Notification<>,
  bundleNotifications?: Array<Notification<Braindate>>,
};

const useStyles = createUseThemeStyles(styles);

const ExpressionOfGratitudePostBraindate = ({
  notification,
  bundleNotifications,
}: Props) => {
  const {
    braindate,
  }: {
    braindate: Braindate | null,
  } = notification.data || {
    braindate: null,
  };

  const self = useSelf();
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [homePath] = useRoutes(homeRoute);

  const isBundle = bundleNotifications && bundleNotifications.length > 1;
  /*
  |------------------------------------------------------------------------------
  | States
  |------------------------------------------------------------------------------
  */
  const [isOpen, setIsOpen] = React.useState(true);

  const handleClose = () => {
    if (isBundle && bundleNotifications) {
      return dispatch(dismissNotificationsBundle(bundleNotifications));
    }
    return dispatch(dismissNotification(notification));
  };

  const onModalClose = () => {
    setIsOpen(false);
    handleClose();
    navigate(homePath);
  };

  const openExpressionCreation = () => {
    handleClose();

    if (!braindate || isBundle) {
      return navigate(generatePath(newExpressionOfGratitudeRoute));
    }

    const braindateId = getBraindateId(braindate);
    const participants = self
      ? getBraindateOtherParticipants(braindate, self)
      : [];

    if (participants.length > 1) {
      return navigate(
        generatePath(newExpressionOfGratitudeWithBraindateRoute, {
          braindateId,
        }),
      );
    }

    return navigate(
      generatePath(newExpressionOfGratitudeWithBraindateAndUserRoute, {
        braindateId,
        userId: getUserId(participants[0]),
      }),
    );
  };

  const message = isBundle ? (
    <Markdown>{intl.formatMessage(messages.postBraindateLive)}</Markdown>
  ) : (
    intl.formatMessage(messages.postBraindate)
  );

  return (
    <Modal
      title={intl.formatMessage(messages.expressionOfGratitude)}
      icon={<ExpressionOfGratitudeIcon className={classes.icon} />}
      width={305}
      isOpen={isOpen}
      handleClose={onModalClose}
      handleAccept={openExpressionCreation}
      message={message}
      acceptCTA={intl.formatMessage(messages.postBraindateAction)}
      closeCTA={intl.formatMessage(messages.maybeLater)}
      actionsReversed
    />
  );
};

export default React.memo<Props>(ExpressionOfGratitudePostBraindate);
